import { graphql } from "babel-plugin-relay/macro";
import { useFragment, useLazyLoadQuery, useMutation } from "react-relay";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { NavigateDirectToQuestionsAdvanceContentButton_LearnOpportunityFragment$key } from "@generated/NavigateDirectToQuestionsAdvanceContentButton_LearnOpportunityFragment.graphql";
import { NavigateDirectToQuestionsAdvanceContentButton_Query } from "@generated/NavigateDirectToQuestionsAdvanceContentButton_Query.graphql";
import { NavigateDirectToQuestionsAdvanceContentButton_RestartContentNodeAfterNotYetPassedMutation } from "@generated/NavigateDirectToQuestionsAdvanceContentButton_RestartContentNodeAfterNotYetPassedMutation.graphql";
import { NavigateDirectToQuestionsAdvanceContentButton_RestartContentNodeAfterPassedMutation } from "@generated/NavigateDirectToQuestionsAdvanceContentButton_RestartContentNodeAfterPassedMutation.graphql";
import { NavigateDirectToQuestionsAdvanceContentButton_StartContentNodeMutation } from "@generated/NavigateDirectToQuestionsAdvanceContentButton_StartContentNodeMutation.graphql";
import { NavigateDirectToQuestionsAdvanceContentButton_UserFragment$key } from "@generated/NavigateDirectToQuestionsAdvanceContentButton_UserFragment.graphql";
import { ElementTypeV2 } from "@generated/UseNodeLogic_NodeFragment.graphql";
import { NavigationButtonLabel } from "./left-frame/NavigationButtonLabel";
import { PrimaryButton } from "../../infecto-lms-webapp/components/buttons/PrimaryButton";
import { formatDateTime } from "../../infecto-lms-webapp/components/DateTimeDisplay.component";
import { isTemporarilyBlockedUntilUtil } from "../../infecto-lms-webapp/utils/advancement-result/is-temporarily-blocked-until";
import { selectTooltipStartContentConfigType } from "../../infecto-lms-webapp/utils/selectTooltipStartContentConfigType";
import { isPermanentlyBlockedUtil } from "../utils/advancement-result/is-permanently-blocked";
import { elementTypeToNextButtonText } from "../utils/elementTypeToNextButtonText";
import { selectCorrectPathToContent } from "../utils/selectCorrectPathToContent";

const QUERY = graphql`
	query NavigateDirectToQuestionsAdvanceContentButton_Query($learnOpportunityId: ID!) {
		node(id: $learnOpportunityId) {
			id
			... on LearnOpportunityV2 {
				id
				...NavigateDirectToQuestionsAdvanceContentButton_LearnOpportunityFragment
			}
		}
		Viewer {
			Auth {
				currentUser {
					user {
						...NavigateDirectToQuestionsAdvanceContentButton_UserFragment
					}
				}
			}
		}
	}
`;

const USER_FRAGMENT = graphql`
	fragment NavigateDirectToQuestionsAdvanceContentButton_UserFragment on User {
		extension(userExtensionKind: InfectopharmUserExtension) {
			... on InfectopharmUserExtension {
				globalUserStatus
			}
		}
	}
`;

const LEARN_OPPORTUNITY_FRAGMENT = graphql`
	fragment NavigateDirectToQuestionsAdvanceContentButton_LearnOpportunityFragment on LearnOpportunityV2 {
		id
		root {
			id
		}
		shortDescription
		structureDefinition {
			title
		}
		typeDefinition {
			... on LearnOpportunityELearningContentTypeDefinition {
				definitionType
				contentNodeAdvancementResult {
					status
					... on CanNotBeStartedContentNodeAdvancementResult {
						reasonConfigTypes
					}
					... on ContinueContentNodeAdvancementResult {
						activeContentSubmissionId
					}
					... on CanNotBeRestartedAfterFailedContentNodeAdvancementResult {
						status
						configResults {
							configType

							... on RestartIfFailedContentConfigResult {
								canBeRestarted
							}

							... on NegativeBlockPermanentlyWhenRewardedRestartIfFailedContentConfigResult {
								canBeRestarted
							}

							... on NegativeBlockTemporarilyWhenRewardedRestartIfFailedContentConfigResult {
								blockedUntil
							}
						}
					}
				}
				containedElementTypes
			}
		}
	}
`;

const START_CONTENT_NODE_MUTATION = graphql`
	mutation NavigateDirectToQuestionsAdvanceContentButton_StartContentNodeMutation(
		$input: StartContentNodeInput!
	) {
		LearnV2 {
			startContentNode(input: $input) {
				contentSubmission {
					id
				}
			}
		}
	}
`;

const RESTART_CONTENT_NODE_AFTER_PASSED_MUTATION = graphql`
	mutation NavigateDirectToQuestionsAdvanceContentButton_RestartContentNodeAfterPassedMutation(
		$input: RestartContentNodeAfterPassedInput!
	) {
		LearnV2 {
			restartContentNodeAfterPassed(input: $input) {
				contentSubmission {
					id
				}
			}
		}
	}
`;

const RESTART_CONTENT_NODE_AFTER_NOT_YET_PASSED_MUTATION = graphql`
	mutation NavigateDirectToQuestionsAdvanceContentButton_RestartContentNodeAfterNotYetPassedMutation(
		$input: RestartContentNodeAfterFailedInput!
	) {
		LearnV2 {
			restartContentNodeAfterFailed(input: $input) {
				contentSubmission {
					id
				}
			}
		}
	}
`;

interface OwnProps {
	learnOpportunityId?: string;
	className?: string;
	elementTypeLabel?: string;
}

export function NavigateDirectToQuestionsAdvanceContentButton({
	learnOpportunityId,
	className,
	elementTypeLabel,
}: OwnProps) {
	const navigate = useNavigate();

	const query = useLazyLoadQuery<NavigateDirectToQuestionsAdvanceContentButton_Query>(
		QUERY,
		{
			learnOpportunityId: learnOpportunityId!,
		},
		{ fetchPolicy: "network-only" },
	);

	const learnOpportunity =
		useFragment<NavigateDirectToQuestionsAdvanceContentButton_LearnOpportunityFragment$key>(
			LEARN_OPPORTUNITY_FRAGMENT,
			query.node!,
		)!;

	const user = useFragment<NavigateDirectToQuestionsAdvanceContentButton_UserFragment$key>(
		USER_FRAGMENT,
		query?.Viewer.Auth.currentUser?.user!, //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
	);

	const [startContentNode, isStartingContentNode] =
		useMutation<NavigateDirectToQuestionsAdvanceContentButton_StartContentNodeMutation>(
			START_CONTENT_NODE_MUTATION,
		);

	const [restartContentNodeAfterPassed, isRestartingContentNodeAfterPassed] =
		useMutation<NavigateDirectToQuestionsAdvanceContentButton_RestartContentNodeAfterPassedMutation>(
			RESTART_CONTENT_NODE_AFTER_PASSED_MUTATION,
		);

	const [restartContentNodeAfterNotYetPassed, isRestartingContentNodeAfterNotYetPassed] =
		useMutation<NavigateDirectToQuestionsAdvanceContentButton_RestartContentNodeAfterNotYetPassedMutation>(
			RESTART_CONTENT_NODE_AFTER_NOT_YET_PASSED_MUTATION,
		);

	const isTemporarilyBlockedUntil = isTemporarilyBlockedUntilUtil(
		learnOpportunity?.typeDefinition.contentNodeAdvancementResult?.configResults,
	);

	const isPermanentlyBlocked = isPermanentlyBlockedUtil(
		learnOpportunity?.typeDefinition.contentNodeAdvancementResult?.configResults,
	);

	const isNoMultipleChoiceElement =
		!learnOpportunity?.typeDefinition.containedElementTypes?.includes("multipleChoice");

	return (
		<div>
			{(() => {
				switch (learnOpportunity.typeDefinition.contentNodeAdvancementResult?.status) {
					case "CanBeStarted":
						return (
							<LinkButton
								disabled={isStartingContentNode}
								onClick={() => {
									localStorage.setItem("previousRouteStarted", location.pathname);
									startContentNode({
										variables: { input: { contentNodeId: learnOpportunity.id } },
										onCompleted: () => {
											navigate(
												selectCorrectPathToContent(
													learnOpportunity?.root?.id,
													learnOpportunity?.id!, //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
													isNoMultipleChoiceElement,
												),
											);
										},
									});
								}}
								className={`bg-primary ${className}`}
							>
								<NavigationButtonLabel
									elementTypeLabel={elementTypeToNextButtonText(
										learnOpportunity.typeDefinition.containedElementTypes,
									)}
									title={learnOpportunity.structureDefinition.title}
									shortDescription={learnOpportunity.shortDescription}
								/>
							</LinkButton>
						);
					case "CanNotBeStarted":
						return (
							<LinkButton
								disabled
								className={`${className}`}
								tooltipOptions={{
									showOnDisabled: true,
									position: "bottom",
									mouseTrack: true,
									mouseTrackTop: 0,
									mouseTrackLeft: 0,
								}}
								tooltip={selectTooltipStartContentConfigType(
									learnOpportunity.typeDefinition.contentNodeAdvancementResult?.reasonConfigTypes,
									user.extension?.globalUserStatus,
								)}
							>
								<NavigationButtonLabel
									elementTypeLabel={elementTypeToNextButtonText(
										learnOpportunity.typeDefinition.containedElementTypes,
									)}
									title={learnOpportunity.structureDefinition.title}
									shortDescription={learnOpportunity.shortDescription}
								/>
							</LinkButton>
						);
					case "Continue":
						return (
							<LinkButton
								onClick={() => {
									localStorage.setItem("previousRouteContinue", location.pathname);
									navigate(
										selectCorrectPathToContent(
											learnOpportunity?.root?.id,
											learnOpportunity?.id!, //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
											isNoMultipleChoiceElement,
										),
									);
								}}
								className={`bg-primary ${className}`}
							>
								<NavigationButtonLabel
									elementTypeLabel={elementTypeToNextButtonText(
										learnOpportunity.typeDefinition.containedElementTypes,
									)}
									title={learnOpportunity.structureDefinition.title}
									shortDescription={learnOpportunity.shortDescription}
								/>
							</LinkButton>
						);
					case "CanBeRestartedAfterPassed":
						return (
							<LinkButton
								disabled={isRestartingContentNodeAfterPassed}
								onClick={() => {
									localStorage.setItem("previousRouteAfterPassed", location.pathname);
									restartContentNodeAfterPassed({
										variables: { input: { contentNodeId: learnOpportunity.id } },
										onCompleted: () => {
											navigate(
												selectCorrectPathToContent(
													learnOpportunity?.root?.id,
													learnOpportunity?.id!, //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
													isNoMultipleChoiceElement,
												),
											);
										},
									});
								}}
								className={`bg-primary ${className}`}
							>
								<NavigationButtonLabel
									elementTypeLabel={elementTypeToNextButtonText(
										learnOpportunity.typeDefinition.containedElementTypes,
									)}
									title={learnOpportunity.structureDefinition.title}
									shortDescription={learnOpportunity.shortDescription}
								/>
							</LinkButton>
						);
					case "CanNotBeRestartedAfterPassed":
						return (
							<LinkButton
								disabled
								className={`bg-primary ${className}`}
								tooltipOptions={{
									showOnDisabled: true,
									position: "bottom",
									mouseTrack: true,
									mouseTrackTop: 0,
									mouseTrackLeft: 0,
								}}
								tooltip={"Modul bereits abgeschlossen"}
							>
								<NavigationButtonLabel
									elementTypeLabel={elementTypeLabel}
									title={learnOpportunity.structureDefinition.title}
									shortDescription={learnOpportunity.shortDescription}
								/>
							</LinkButton>
						);
					case "CanBeRestartedAfterFailed":
						return (
							<LinkButton
								disabled={isRestartingContentNodeAfterNotYetPassed}
								onClick={() => {
									localStorage.setItem("previousRouteAfterFailed", location.pathname);
									restartContentNodeAfterNotYetPassed({
										variables: { input: { contentNodeId: learnOpportunity.id } },
										onCompleted: () => {
											navigate(
												selectCorrectPathToContent(
													learnOpportunity?.root?.id,
													learnOpportunity?.id!, //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
													isNoMultipleChoiceElement,
												),
											);
										},
									});
								}}
								className={`bg-primary ${className}`}
							>
								<NavigationButtonLabel
									elementTypeLabel={elementTypeToNextButtonText(
										learnOpportunity.typeDefinition.containedElementTypes,
									)}
									title={learnOpportunity.structureDefinition.title}
									shortDescription={learnOpportunity.shortDescription}
								/>
							</LinkButton>
						);
					case "CanNotBeRestartedAfterFailed":
						const tooltip = (() => {
							if (isTemporarilyBlockedUntil) {
								return `Dieses Modul ist nach Falschbeantwortung bis ${formatDateTime(
									isTemporarilyBlockedUntil.blockedUntil as string,
								)} für Sie gesperrt. Sie erhalten eine E-Mail sobald es wieder freigeschaltet wurde.`;
							}
							if (isPermanentlyBlocked) {
								return "Sie haben keinen weiteren Versuch mehr.";
							}
							return "Sie haben keinen weiteren Versuch mehr.";
						})();
						return (
							<LinkButton
								disabled
								className={`bg-primary ${className}`}
								tooltipOptions={{
									showOnDisabled: true,
									position: "bottom",
									mouseTrack: true,
									mouseTrackTop: 0,
									mouseTrackLeft: 0,
								}}
								tooltip={tooltip}
							>
								<NavigationButtonLabel
									elementTypeLabel={elementTypeToNextButtonText(
										learnOpportunity.typeDefinition.containedElementTypes,
									)}
									title={learnOpportunity.structureDefinition.title}
									shortDescription={learnOpportunity.shortDescription}
								/>
							</LinkButton>
						);
					default:
						// BlockedByTree, NotContentNode, ViolatesTreeFlow
						return null;
				}
			})()}
		</div>
	);
}

const LinkButton = styled(PrimaryButton)`
	display: flex;
	width: 100%;
	justify-content: center;
	text-align: center;
	border: none;
	cursor: pointer;
	flex: 1 1 auto;
`;
